/*
 * Copyright (C) 2019 Frontier Developments PLC.
 */

import {Component} from 'vue-property-decorator';
import {Action, Getter} from 'vuex-class';
import pkce from '@/services/pkce';
import {
    ContributionConfigInterface,
    ContributionInterface, Contributions,
    FileInterface, IssueFilterInterface,
    IssueInterface, IssuesConfigInterface,
} from '@/store/modules/issues/types';
import FileDragDrop from '@/components/MediaDragDrop/index.vue';
import {LoadingState, LoadingStateInterface} from '@/components/Loading/types';
import {mixins} from 'vue-class-component';
import Captcha from '@/mixins/Captcha';
import Loading from '@/components/Loading/index.vue';
import IssueTitleInfo from '@/components/IssueTitleInfo/index.vue';
import {AxiosResponse} from 'axios';
import {api} from '@/services/http';
import {GameInterface} from '@/store/modules/games/types';
const namespace: string = 'issuesStateModule';
import IssueVote from '@/components/IssueVote/index.vue';

@Component({
    name: 'c-comments-form',
    components: {
        'c-file-drag-drop': FileDragDrop,
        'c-loading': Loading,
        'c-issue-title-info': IssueTitleInfo,
        'c-issue-vote': IssueVote,
    },
})
export default class CommentsForm extends mixins(Captcha) {
    @Action('fetchIssues', {namespace})
    public fetchIssues!: (issuesConfig: IssuesConfigInterface) => Promise<void>;
    @Action('fetchIssue', {namespace: 'issuesStateModule'})
    public fetchIssue!: (issueId: number) => Promise<void>;
    @Action('updateSearchTerm', {namespace: 'issuesStateModule'})
    public updateSearchTerm!: (term: string) => void;
    @Getter('getCurrentGame', {namespace: 'gamesStateModule'})
    public getCurrentGame!: GameInterface;
    @Getter('updateCurrentGameVersion', {namespace: 'gamesStateModule'})
    public updateCurrentGameVersion!: number | null;
    @Getter('getIssues', {namespace})
    public getIssues!: (stateName: string) => IssueInterface[];
    @Getter('authenticated', {namespace: 'userSessionModule'}) public authenticated!: any;
    @Action('createContribution', {namespace: 'issuesStateModule'}) public createContribution: any;
    @Action('createMedia', {namespace: 'issuesStateModule'}) public createMedia: any;
    @Action('createEvidence', {namespace: 'issuesStateModule'}) public createEvidence: any;
    @Action('uploadToS3', {namespace: 'issuesStateModule'}) public uploadToS3: any;
    @Action('fetchContributors', {namespace: 'issuesStateModule'})
    public fetchContributors!: (contributionConfig: ContributionConfigInterface) => Promise<void>;
    @Action('subscribe', {namespace: 'formStateModule'}) public subscribe: any;

    @Getter('getCurrentIssue', {namespace: 'issuesStateModule'}) public issue!: IssueInterface;

    public readonly MAX_FILE_SIZE_IMAGE: number = 104857600;
    public readonly MAX_FILE_COUNT: number = 4;

    public description: string = '';
    public reproductionSteps: string = '';
    public canReproduce: string = '';
    public images: File[]|null = null;
    public state: LoadingStateInterface = new LoadingState(false, false);
    // Your Subscription
    public subscription: boolean = false;
    public listOfDuplicateIssues: any[] = [];
    public searchString: string = '';
    public debounceInput: Debounce = this.$debounce(this.keywordChanged, 500);
    public filter: IssueFilterInterface = {
        orderByDate : '',
        orderByVote: '',
        status: '',
        platform: '',
        itemsPerPage: 10,
        searchTerm: '',
        gameId: null,
        category: '',
        customerId: '',
        version: null,
    };
    public versionList: any = [];
    public showIssueSummary: boolean = false;
    public stateName: string = 'search';
    public selectedDuplicateIssues: any[] = [];

    /**
     * Issues Getter
     */
    public get issues(): IssueInterface[] {
        return this.getIssues(`${this.stateName}Issues`);
    }

    public mounted(): void {
        this.reloadRecaptcha();
    }

    /**
     * Contribution submission.
     * @param {string} token
     * @return {Promise<void>}
     */
    public async onVerify(token: string): Promise<void> {
        this.captchaResponse = token;
        try {
            this.state.start();
            const contribution: ContributionInterface = {
                creator_type: 'contributor',
                can_reproduce: (this.canReproduce === '1'),
                description: this.description,
                how_to_reproduce: this.reproductionSteps || '',
                issue: `/api/issues/${this.issue.id}`,
                captcha_token: token,
            };

            // create the issue
            const contributionId: number = await this.createContribution(contribution);

            if (contribution.can_reproduce && !!this.images) {
                try {
                    await this.uploadAndLinkImages(contribution, contributionId, this.images || []);
                } catch (e) {
                    // do nothing
                }
            }


            if (this.subscription) {
                await this.subscribe(this.issue.id);
            }

            await this.fetchContributors({
                    issueId: this.issue.id,
                    itemsPerPage: Contributions.ITEMS_PER_PAGE,
                });
            this.resetForm();
            this.state.complete();
            this.$toastr.success(this.$i18n.translate('view.issueDetails.comment.save.success',
                {}) || '');
            await this.$validator.reset();
        } catch (e) {
            this.$toastr.error(this.$i18n.translate('view.issueDetails.comment.save.error',
                {}) || '');
            this.state.failed();
        }
    }

    /**
     * Upload the images and link to issue detail.
     * @param {ContributionInterface} contribution
     * @param {number} contributionId
     * @param {File[]} images
     * @return {Promise<void>}
     */
    public async uploadAndLinkImages(
        contribution: ContributionInterface,
        contributionId: number,
        images: File[],
    ): Promise<void> {
        const evidenceIds: number[] = [];

        for (const file of images) {
            // get pre-signed urls for images
            const type: string = file.type ? file.type : 'text/plain';

            const fileItem: FileInterface = await this.createMedia({
                name: file.name,
                mime_type: type,
                size: file.size,
                path: `/issue_details/${contributionId}`,
            });

            // upload the images
            try {
                await this.uploadToS3({preSignedUrl: fileItem.presignedUrl, file});
                evidenceIds.push(fileItem.id);
            } catch (e) {
                // ignore exception and continue
            }
        }

        // link the images
        if (evidenceIds.length > 0) {
            await this.createEvidence({
                fileIdsData: evidenceIds,
                issueDetailsId: contributionId,
            });
        }
    }

    /**
     * Reset the form.
     */
    public resetForm(): void {
        this.canReproduce = '';
        this.description = '';
        this.reproductionSteps = '';
        this.images = [];
    }

    /**
     * Validation of form.
     * @returns {Promise<boolean>}
     */
    public async validate(): Promise<boolean> {
        try {
            // tidy up data
            this.description = this.description.trim();
            this.reproductionSteps = this.reproductionSteps.trim();

            // refresh files to check for out of browser update
            this.$validator.errors.clear();

            // const rules:
            const result: object|boolean = await this.$validator.validateAll();

            return !!result;
        } catch (e) {
            return false;
        }
    }

    /**
     * File change event.
     *
     * @param {string} fieldName
     * @param {FileList | File[]} fileList
     */
    public filesChange(fieldName: string, fileList: FileList|File[]): void {
        // todo: validation?
    }

    /**
     * Login action event
     */
    public loginUser(): void {
        window.location.href = pkce.getAuthUrl();
    }

    /**
     *  Progress to the next step if form values are valid.
     * @return {Promise<void>}
     */
    public async validateAndSubmit(): Promise<void> {
        // Submit issue via recaptcha
        if (!await this.validate()) {
            return;
        }

        for (const duplicateIssue of this.selectedDuplicateIssues) {
            try {
                await api.axios.post('/api/issue_duplicates',
                    {
                        issue: '/api/issues/' + this.issue.id,
                        issue_duplicate: '/api/issues/' + duplicateIssue.id,
                    },
                );
            } catch (e) {
                // console.log(e);
            }
        }
        this.submit();
    }

    /**
     * Get the url of the duplicate issue
     * @param potentialDuplicateIssue
     */
    public async getDuplicateIssueEntered(potentialDuplicateIssue: string): Promise<void> {
        const issueResponse: AxiosResponse =
            await api.axios.get(`api/issues/${potentialDuplicateIssue}?timestamp=${new Date().getTime()}`);

        const newIssue: any = this.formatDuplicateIssuesToDisplay(issueResponse);

        this.listOfDuplicateIssues = [];
        if (!this.selectedDuplicateIssues.some((selectedDuplicateIssue: any) =>
            selectedDuplicateIssue.id === newIssue.id )) {
            this.listOfDuplicateIssues.push(newIssue);
        }
    }

    /**
     * Fetch Filtered Issues
     */
    public async fetchFilteredIssues(): Promise<void> {
        if (this.filter.gameId === undefined) {
            this.filter.gameId = undefined !== this.getCurrentGame ? this.getCurrentGame.id : undefined;
        }

        if (typeof this.updateCurrentGameVersion !== 'undefined' && this.updateCurrentGameVersion !== null ) {
            this.filter.version = this.updateCurrentGameVersion;
        }
        // if no game is selected, remove the filter on version as well
        if (typeof this.filter.gameId === 'undefined') {
            this.filter.version = null;
        }

        this.filter.orderByDate = 'desc';

        await this.fetchIssues({
            filters: this.filter,
            stateName: 'search',
            showIssueSummary: false,
        });

        this.listOfDuplicateIssues = [];
        for (const issue of this.issues) {
            if (this.listOfDuplicateIssues.length < 10 && !this.selectedDuplicateIssues.some(
                (selectedDuplicateIssue: any) => selectedDuplicateIssue.id === issue.id )) {
                this.listOfDuplicateIssues.push(issue);
            }
        }
    }

    /**
     * Keyword changed event.
     * @param {string} keyword
     */
    public async keywordChanged(keyword?: string): Promise<void> {
        if ( this.searchString.includes('/issue-detail/')) {
            const splitted: any = this.searchString.split('/');
            const issueId: any = splitted[ splitted.length - 1 ];
            if (typeof issueId !== 'undefined' && issueId.replace(/\s/g, '').length > 0) {
                await this.getDuplicateIssueEntered(issueId);
            }
        }

        const splitCategory: string[] = this.issue.category.id.split('/');
        const categoryId: string = splitCategory[ splitCategory.length - 1 ];

        if ( !this.searchString.includes('/issue-detail/')) {
            try {
                this.updateSearchTerm(this.searchString);
                this.filter.searchTerm = this.searchString;
                this.filter.category = categoryId;

                // don't refresh the search if it's an empty string / it's already been searched before
                if (typeof keyword !== 'undefined' && keyword.replace(/\s/g, '').length > 2) {
                    await this.fetchFilteredIssues();
                }
                if (typeof keyword !== 'undefined' && keyword.replace(/\s/g, '').length === 0) {
                    this.listOfDuplicateIssues = [];
                }
            } catch (e) {
                // console.log(e);
            }
        }
    }

    /**
     * Open the related issues in new windows
     * @param id
     */
    public openNewWindow(id: string): void {
        window.open('/issue-detail/' + id);
    }

    /**
     * Add to the selected list of duplicate issues
     * @param potentialDuplicateIssue
     */
    public async addToSelectedDuplicateIssues(potentialDuplicateIssue: number): Promise<void> {
        const issueResponse: AxiosResponse =
            await api.axios.get(`api/issues/${potentialDuplicateIssue}?timestamp=${new Date().getTime()}`);

        const newIssue: any = this.formatDuplicateIssuesToDisplay(issueResponse);

        this.selectedDuplicateIssues.push(newIssue);
    }

    /**
     * Format the issues so can display them properly on the frontend
     * @param issueResponse
     */
    public formatDuplicateIssuesToDisplay(issueResponse: AxiosResponse): any {
        const newIssue: any = issueResponse.data;
        newIssue.game = newIssue.category.game_name;
        newIssue.tags = newIssue.platforms;
        newIssue.version = newIssue.version.name;
        return newIssue;
    }

    /**
     * Remove issue
     * @param issue
     */
    public removeIssueFromSelectedDuplicateList(issue: any): void {
        for (let i: number = 0; i < this.selectedDuplicateIssues.length; i++) {
            if ( this.selectedDuplicateIssues[i] === issue) {
                this.selectedDuplicateIssues.splice(i, 1);
            }
        }
    }
}
